import { useCallback, useEffect, useState } from "react";
import { isFreshAuthentication } from "../../features/organizations/workspace-entry.js";
import {
  documentEntryHoldKind,
  markLoaderHoldElapsed,
  settleLoginPageRefreshHold,
  startLoaderHold,
  type LoaderHold,
  type LoaderHoldKind,
} from "./feedbot-loader-timing.js";

function readNavigationType(): string | undefined {
  if (typeof PerformanceNavigationTiming === "undefined") return undefined;
  const [entry] = performance.getEntriesByType("navigation");
  return entry instanceof PerformanceNavigationTiming ? entry.type : undefined;
}

interface FeedBotLoaderHold {
  hold: LoaderHold | null;
  start: (kind: LoaderHoldKind) => void;
  /** A Login refresh found an authenticated session: switch to page-refresh timing. */
  settleLoginPageRefresh: () => void;
  release: () => void;
}

/**
 * The single minimum-duration timer for the app bootstrap loader. The document
 * load starts one hold on mount; sign-in and workspace entry start the others.
 * The caller decides when a hold applies and releases it.
 *
 * `onLoginPage` is read once, on mount, to tell a Login refresh apart.
 */
export function useFeedBotLoaderHold({ onLoginPage }: { onLoginPage: boolean }): FeedBotLoaderHold {
  const [hold, setHold] = useState<LoaderHold | null>(() =>
    startLoaderHold(
      null,
      documentEntryHoldKind({
        navigationType: readNavigationType(),
        freshAuthentication: isFreshAuthentication(),
        onLoginPage,
      }),
      performance.now(),
    ),
  );

  const pendingEndsAt = hold !== null && !hold.minimumElapsed ? hold.endsAt : null;

  useEffect(() => {
    if (pendingEndsAt === null) return;
    const timer = window.setTimeout(
      () => setHold((current) => markLoaderHoldElapsed(current, pendingEndsAt)),
      Math.max(0, pendingEndsAt - performance.now()),
    );
    return () => window.clearTimeout(timer);
  }, [pendingEndsAt]);

  const start = useCallback((kind: LoaderHoldKind) => {
    setHold((current) => startLoaderHold(current, kind, performance.now()));
  }, []);

  const settleLoginPageRefresh = useCallback(() => {
    setHold((current) => settleLoginPageRefreshHold(current, performance.now()));
  }, []);

  const release = useCallback(() => setHold(null), []);

  return { hold, start, settleLoginPageRefresh, release };
}
